import React, { useState } from 'react'
import DashModalV2 from '../../../components/DashModalV2'
import { Btn, COLORS, Chip, EmptyState, Field, INPUT_STYLE, lb, useSaving } from '../FieldOpsKit'

/**
 * Drivers & vehicles: add, edit and deactivate. A driver is a person with a (default) vehicle; the vehicle's capacity
 * only warns on the delivery board, it never blocks. Deactivating is blocked while open deliveries are assigned.
 */
export default function FleetTab({ ctx }) {
    const drv = ctx.state.drivers
    const [driverModal, setDriverModal] = useState(null)   // null | {} (new) | driver
    const [vehicleModal, setVehicleModal] = useState(null)
    if (!ctx.can.dispatch) return <EmptyState title="Dispatchers only" text="Only administrators can manage drivers and vehicles." />

    const openFor = (driverId) => drv.deliveries.filter((d) => d.driverId === driverId && (d.status === 'planned' || d.status === 'out')).length
    const driversOn = (vehicleId) => drv.drivers.filter((d) => d.active && d.vehicleId === vehicleId)

    const toggleDriver = (d) => {
        if (d.active && openFor(d.id) > 0) { ctx.toast(d.name + ' still has ' + openFor(d.id) + ' open deliveries. Reassign them first.', 'error'); return }
        ctx.dispatch({ type: 'DRIVER_ACTIVE', id: d.id, active: !d.active })
        ctx.toast(d.name + (d.active ? ' deactivated.' : ' reactivated.'))
    }
    const toggleVehicle = (v) => {
        if (v.active && driversOn(v.id).length > 0) { ctx.toast(v.name + ' is the vehicle of ' + driversOn(v.id).map((d) => d.name).join(', ') + '. Change their vehicle first.', 'error'); return }
        ctx.dispatch({ type: 'VEHICLE_ACTIVE', id: v.id, active: !v.active })
        ctx.toast(v.name + (v.active ? ' deactivated.' : ' reactivated.'))
    }

    return (
        <div className="row g-4">
            <div className="col-lg-7">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h6 className="card-title-clean mb-0">Drivers</h6><Btn small onClick={() => setDriverModal({})}>+ Add driver</Btn>
                </div>
                <div className="tableFixHeadSmall"><table className="table table-clean">
                    <thead><tr><th>Driver</th><th>Phone</th><th>Vehicle</th><th>Open</th><th>Status</th><th></th></tr></thead>
                    <tbody>{drv.drivers.map((d) => {
                        const v = drv.vehicles.filter((x) => x.id === d.vehicleId)[0]
                        return (
                            <tr key={d.id} style={{ opacity: d.active ? 1 : 0.55 }}>
                                <td style={{ fontWeight: 700 }}>{d.name}</td><td>{d.phone}</td><td>{v ? v.name : '—'}</td><td>{openFor(d.id)}</td>
                                <td><Chip tone={d.active ? 'green' : 'gray'}>{d.active ? 'Active' : 'Inactive'}</Chip></td>
                                <td style={{ whiteSpace: 'nowrap' }}><Btn small kind="secondary" onClick={() => setDriverModal(d)}>Edit</Btn> <Btn small kind="secondary" onClick={() => toggleDriver(d)}>{d.active ? 'Deactivate' : 'Reactivate'}</Btn></td>
                            </tr>
                        )
                    })}</tbody>
                </table></div>
            </div>
            <div className="col-lg-5">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h6 className="card-title-clean mb-0">Vehicles</h6><Btn small onClick={() => setVehicleModal({})}>+ Add vehicle</Btn>
                </div>
                <div className="tableFixHeadSmall"><table className="table table-clean">
                    <thead><tr><th>Vehicle</th><th>Type</th><th>Capacity</th><th>Status</th><th></th></tr></thead>
                    <tbody>{drv.vehicles.map((v) => (
                        <tr key={v.id} style={{ opacity: v.active ? 1 : 0.55 }}>
                            <td style={{ fontWeight: 700 }}>{v.name}</td><td>{v.type}</td><td>{lb(v.capacityLb)}</td>
                            <td><Chip tone={v.active ? 'green' : 'gray'}>{v.active ? 'Active' : 'Inactive'}</Chip></td>
                            <td style={{ whiteSpace: 'nowrap' }}><Btn small kind="secondary" onClick={() => setVehicleModal(v)}>Edit</Btn> <Btn small kind="secondary" onClick={() => toggleVehicle(v)}>{v.active ? 'Off' : 'On'}</Btn></td>
                        </tr>
                    ))}</tbody>
                </table></div>
                <div style={{ fontSize: '0.72rem', color: COLORS.muted, fontStyle: 'italic', marginTop: 8 }}>Capacity only warns when a driver's load for the day goes over it; it never blocks an assignment.</div>
            </div>
            {driverModal ? <DriverModal ctx={ctx} initial={driverModal} onClose={() => setDriverModal(null)} /> : null}
            {vehicleModal ? <VehicleModal ctx={ctx} initial={vehicleModal} onClose={() => setVehicleModal(null)} /> : null}
        </div>
    )
}

function DriverModal({ ctx, initial, onClose }) {
    const drv = ctx.state.drivers
    const isNew = !initial.id
    const [name, setName] = useState(initial.name || '')
    const [phone, setPhone] = useState(initial.phone || '')
    const [vehicleId, setVehicleId] = useState(initial.vehicleId || '')
    const [tried, setTried] = useState(false)
    const [busy, run] = useSaving()
    const digits = phone.replace(/[^0-9]/g, '')
    const errors = { name: name.trim().length < 2 ? 'Enter the driver name' : '', phone: digits.length < 7 ? 'Enter a valid phone number' : '', vehicle: !vehicleId ? 'Pick a vehicle' : '' }
    const submit = () => {
        setTried(true)
        if (errors.name || errors.phone || errors.vehicle) return
        run(() => { ctx.dispatch({ type: 'DRIVER_SAVE', driver: { id: initial.id, name: name.trim(), phone: phone.trim(), vehicleId: vehicleId } }); ctx.toast(name.trim() + (isNew ? ' added.' : ' updated.')); onClose() })
    }
    return (
        <DashModalV2 show onClose={onClose} title={isNew ? 'Add driver' : 'Edit driver'} size="md"
            footer={<React.Fragment><Btn kind="secondary" onClick={onClose}>Cancel</Btn><Btn busy={busy} onClick={submit}>{isNew ? 'Add driver' : 'Save changes'}</Btn></React.Fragment>}>
            <Field label="Name" error={tried ? errors.name : ''}><input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }} /></Field>
            <Field label="Phone" error={tried ? errors.phone : ''}><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 010-0000" style={{ ...INPUT_STYLE, width: '100%' }} /></Field>
            <Field label="Default vehicle" error={tried ? errors.vehicle : ''}><select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} style={{ ...INPUT_STYLE, width: '100%' }}>
                <option value="">Select a vehicle…</option>
                {drv.vehicles.filter((v) => v.active || v.id === vehicleId).map((v) => <option key={v.id} value={v.id}>{v.name} · {v.type} · {lb(v.capacityLb)}</option>)}
            </select></Field>
        </DashModalV2>
    )
}

function VehicleModal({ ctx, initial, onClose }) {
    const isNew = !initial.id
    const [name, setName] = useState(initial.name || '')
    const [type, setType] = useState(initial.type || '')
    const [cap, setCap] = useState(initial.capacityLb ? String(initial.capacityLb) : '')
    const [tried, setTried] = useState(false)
    const [busy, run] = useSaving()
    const errors = { name: name.trim().length < 2 ? 'Enter a name' : '', type: type.trim().length < 2 ? 'Enter the type' : '', cap: !(Number(cap) > 0) ? 'Capacity must be greater than 0' : '' }
    const submit = () => {
        setTried(true)
        if (errors.name || errors.type || errors.cap) return
        run(() => { ctx.dispatch({ type: 'VEHICLE_SAVE', vehicle: { id: initial.id, name: name.trim(), type: type.trim(), capacityLb: Math.round(Number(cap)) } }); ctx.toast(name.trim() + (isNew ? ' added.' : ' updated.')); onClose() })
    }
    return (
        <DashModalV2 show onClose={onClose} title={isNew ? 'Add vehicle' : 'Edit vehicle'} size="md"
            footer={<React.Fragment><Btn kind="secondary" onClick={onClose}>Cancel</Btn><Btn busy={busy} onClick={submit}>{isNew ? 'Add vehicle' : 'Save changes'}</Btn></React.Fragment>}>
            <Field label="Name" error={tried ? errors.name : ''}><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Truck 20" style={{ ...INPUT_STYLE, width: '100%' }} /></Field>
            <Field label="Type" error={tried ? errors.type : ''}><input type="text" value={type} onChange={(e) => setType(e.target.value)} placeholder="26 ft box" style={{ ...INPUT_STYLE, width: '100%' }} /></Field>
            <Field label="Capacity (lb)" error={tried ? errors.cap : ''}><input type="number" min="1" value={cap} onChange={(e) => setCap(e.target.value)} style={{ ...INPUT_STYLE, width: 160 }} /></Field>
        </DashModalV2>
    )
}
